import { useEffect, useState } from 'react'

import { api } from '@/lib/api'
import type { AccountsState, MyAccount } from '@/lib/types'

/**
 * Sélecteur du compte actif, dans le bandeau du haut.
 *
 * Il répond en permanence à LA question qui compte avant d'appuyer : « sur
 * quel compte part cet ordre ? ». Un compte Capital.com de démonstration et
 * un compte de challenge FTMO n'ont pas les mêmes règles, ni les mêmes
 * conséquences — les confondre coûterait un challenge. Le nom du courtier
 * est donc toujours visible, et un compte réel est signalé en rouge.
 *
 * Changer de compte recharge tout ce qui dépend du compte — prix, positions,
 * statistiques, ticket — plutôt que de garder à l'écran des chiffres de
 * l'ancien compte le temps que les nouveaux arrivent.
 */
export function AccountSwitcher({
  state,
  onChange,
}: {
  state: AccountsState | null
  onChange: (state: AccountsState) => void
}) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Échap referme la liste, comme toute vue modale au clavier.
  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  if (state === null) return null

  const active = state.accounts.find((account) => account.id === state.active_id) ?? null
  const several = state.accounts.length > 1

  async function choose(account: MyAccount) {
    if (account.id === state?.active_id) {
      setOpen(false)
      return
    }
    setBusy(account.id)
    setError(null)
    try {
      onChange(await api.setActiveAccount(account.id))
      setOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Changement refusé')
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        className="flex min-h-touch min-w-0 max-w-[58vw] items-center gap-1.5 rounded-md border border-border bg-elevated px-2 text-left hover:bg-border/60 active:bg-border sm:max-w-[320px]"
      >
        <StatusDot account={active} />
        <span className="min-w-0">
          <span className="block truncate text-[12px] font-semibold leading-tight text-ink-strong">
            {active ? active.display_name : 'Aucun compte'}
          </span>
          {active && (
            <span className="block truncate text-[10px] leading-tight text-ink-muted">
              {active.broker_name || providerLabel(active)}
              {' · '}
              <span className={active.is_live ? 'font-bold text-down' : ''}>
                {active.is_live ? 'RÉEL' : 'démo'}
              </span>
            </span>
          )}
        </span>
        {several && <span className="shrink-0 text-[11px] text-ink-muted">▾</span>}
      </button>

      {open && (
        // Centrée et bornée à la hauteur visible, comme la confirmation
        // d'ordre : collée en bas, la barre de Safari en masquait la fin.
        <div
          className="safe-top safe-bottom fixed inset-x-0 top-0 z-50 flex h-[100dvh] items-center justify-center bg-black/70 p-3"
          onClick={() => setOpen(false)}
        >
          <div
            role="dialog"
            aria-label="Choisir le compte de trading"
            className="flex max-h-full w-full max-w-md flex-col overflow-hidden rounded-xl border border-border bg-panel"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="shrink-0 border-b border-border px-4 py-3">
              <p className="text-[15px] font-semibold text-ink-strong">Compte de trading</p>
              <p className="mt-0.5 text-[11px] leading-snug text-ink-muted">
                Tout se fait sur le compte choisi : les prix, les positions, les
                statistiques — <strong className="text-ink">et vos ordres</strong>.
              </p>
            </div>

            <ul className="min-h-0 flex-1 divide-y divide-border overflow-y-auto">
              {state.accounts.length === 0 && (
                <li className="px-4 py-6 text-center text-[13px] text-ink-muted">
                  Aucun compte ne vous est ouvert. Demandez à l'administration de vous en
                  attribuer un.
                </li>
              )}
              {state.accounts.map((account) => {
                const isActive = account.id === state.active_id
                return (
                  <li key={account.id}>
                    <button
                      type="button"
                      disabled={busy !== null}
                      onClick={() => void choose(account)}
                      className={`flex min-h-touch w-full items-center gap-3 px-4 py-2.5 text-left disabled:opacity-60 ${
                        isActive ? 'bg-accent/10' : 'hover:bg-elevated/70 active:bg-elevated'
                      }`}
                    >
                      <StatusDot account={account} />
                      <span className="min-w-0 flex-1">
                        {/*
                          Démo ou réel à côté du NOM, jamais en fin de ligne :
                          un numéro Capital.com de dix-huit chiffres poussait
                          la mention hors de l'écran — et c'est précisément
                          l'information qu'on ne peut pas laisser couper.
                        */}
                        <span className="flex min-w-0 items-center gap-1.5">
                          <span className="truncate text-[14px] font-semibold text-ink-strong">
                            {account.display_name}
                          </span>
                          <LiveTag live={account.is_live} />
                        </span>
                        <span className="num block truncate text-[11px] text-ink-muted">
                          {account.broker_name || providerLabel(account)} · n° {account.external_id}
                        </span>
                        <span className={`block text-[11px] ${statusTone(account)}`}>
                          {busy === account.id ? 'Bascule…' : statusText(account)}
                        </span>
                      </span>
                      {isActive && (
                        <span className="shrink-0 text-[12px] font-semibold text-accent">
                          ✓ actif
                        </span>
                      )}
                    </button>
                  </li>
                )
              })}
            </ul>

            {error && (
              <p className="mx-4 my-2 shrink-0 rounded-md border border-down/40 bg-down/10 p-2 text-[12px] text-down">
                {error}
              </p>
            )}

            <div className="shrink-0 border-t border-border p-3">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="min-h-touch w-full rounded-md border border-border bg-elevated text-[14px] text-ink-muted"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

/** Démo ou réel, en étiquette : le réel en rouge plein, impossible à manquer. */
function LiveTag({ live }: { live: boolean }) {
  return live ? (
    <span className="shrink-0 rounded bg-down px-1.5 py-0.5 text-[10px] font-bold text-white">
      RÉEL
    </span>
  ) : (
    <span className="shrink-0 rounded border border-border px-1.5 py-0.5 text-[10px] text-ink-muted">
      démo
    </span>
  )
}

function providerLabel(account: Pick<MyAccount, 'provider'>): string {
  return account.provider === 'ctrader' ? 'cTrader' : 'Capital.com'
}

function statusText(account: MyAccount): string {
  if (account.connected) return 'connecté'
  if (account.starting) return 'connexion en cours…'
  if (account.error) return `échec : ${account.error}`
  return 'non connecté'
}

function statusTone(account: MyAccount): string {
  if (account.connected) return 'text-up'
  if (account.starting) return 'text-warn'
  if (account.error) return 'text-down'
  return 'text-ink-muted'
}

/** Pastille d'état de la liaison : verte, orange, rouge ou grise. */
function StatusDot({ account }: { account: MyAccount | null }) {
  const tone = !account
    ? 'bg-ink-muted/40'
    : account.connected
      ? 'bg-up'
      : account.starting
        ? 'bg-warn'
        : account.error
          ? 'bg-down'
          : 'bg-ink-muted/60'
  return <span aria-hidden className={`h-2 w-2 shrink-0 rounded-full ${tone}`} />
}
